import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { addDatasetApiKey as addDatasetApiKeyAction } from 'js/actions';
import { getResourceApiKeys } from 'js/lib/ResourceUtils';
import { getUsableApiKey, getUsableApiKeyEntity, isDatasetSyncable } from 'js/lib/URLUtils';
import useAppDispatch from 'js/hooks/useAppDispatch';
import { Dataset } from 'new/components/marketplace/components/DataProductsDrawer/types';

export interface ApiKeyEntity {
  id: string;
  name: string | null;
  api_key: string;
  url: string;
  status: string;
}

export interface UseDatasetApiKeyReturn {
  apiKeys: Record<string, ApiKeyEntity> | null;
  usableApiKey: string | null;
  usableApiKeyEntity: ApiKeyEntity | null;
  isLoading: boolean;
  isSyncable: boolean;
  generateApiKey: () => Promise<void>;
}

/**
 * Custom hook to manage API keys for a dataset.
 *
 * Provides access to dataset API keys, loading state, syncability check, and functions to generate new keys.
 * Uses Redux state for data and memoization for performance optimization.
 *
 * @param dataset - The dataset object (optional, can be null/undefined during loading)
 * @returns Object containing API keys, loading state, syncability flag, and generation function
 */
export function useDatasetApiKey(dataset?: Dataset | null): UseDatasetApiKeyReturn {
  const dispatch = useAppDispatch();
  const reduxApiKeys = useSelector((state: any) => state.entities.apiKeys);
  const request = useSelector((state: any) => state.request);

  const apiKeys = useMemo(() => {
    if (!dataset) return null;
    return getResourceApiKeys(dataset, reduxApiKeys);
  }, [dataset, reduxApiKeys]);

  const usableApiKey = useMemo(() => {
    return getUsableApiKey(apiKeys);
  }, [apiKeys]);

  const usableApiKeyEntity = useMemo(() => {
    return getUsableApiKeyEntity(apiKeys);
  }, [apiKeys]);

  const isSyncable = useMemo(() => {
    return isDatasetSyncable(dataset);
  }, [dataset]);

  const isLoading = Boolean(
    request.CREATE_RESOURCE_API_KEY_REQUEST ||
    (request.GET_DATA_SET_REQUEST && !request.REGISTER_DATASET_ON_CATALOG_REQUEST),
  );

  const generateApiKey = useCallback(async () => {
    if (!dataset?.id) {
      console.error('useDatasetApiKey: Cannot generate API key - dataset ID is missing');
      return;
    }

    try {
      await dispatch(addDatasetApiKeyAction(dataset.id));
    } catch (error) {
      console.error('useDatasetApiKey: Failed to generate API key:', error);
    }
  }, [dataset?.id, dispatch]);

  return {
    apiKeys,
    usableApiKey,
    usableApiKeyEntity,
    isLoading,
    isSyncable,
    generateApiKey,
  };
}
